import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Typography, message, Spin, Select, Button, Tooltip } from 'antd';
import { FileTextOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { tradeAPI } from '../services/api';

const { Title } = Typography;
const { Option } = Select;

const parseJsonSafe = (v) => {
  if (!v) return null;
  if (typeof v === 'object') return v;
  try {
    return JSON.parse(v);
  } catch (e) {
    return null;
  }
};

const getRecordItems = (rawObj) => {
  const items = rawObj?.data?.record_items;
  return Array.isArray(items) ? items : [];
};

const shortenText = (s, maxLen = 180) => {
  const str = String(s ?? '');
  if (str.length <= maxLen) return str;
  return `${str.slice(0, maxLen)}...`;
};

const buildOpText = (rawObj) => {
  try {
    const items = rawObj?.data?.record_items || [];
    if (!Array.isArray(items) || items.length === 0) return '';
    return items
      .map((it) => {
        const stockName = it?.stock_name || '';
        const stockCode = it?.stock_code || '';
        const costPrice = Number(it?.cost_price) / 1000000000;
        const totalRatio = Number(it?.total_ratio) / 1000000000;
        const costText = Number.isFinite(costPrice) ? costPrice.toFixed(3) : '';
        const ratioPct = Number.isFinite(totalRatio) ? (totalRatio * 100).toFixed(2) : '';
        return `${stockName}（${stockCode}）：${costText} - ${ratioPct}%`;
      })
      .join('\n');
  } catch (e) {
    return '';
  }
};

const TopicRecords = () => {
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const fetchTopics = useCallback(async () => {
    try {
      const response = await tradeAPI.getTopics();
      const list = response.data || [];
      setTopics(list);
      if (!selectedTopic && list.length > 0 && list[0] && list[0].id) {
        setSelectedTopic(list[0].id);
      }
    } catch (error) {
      console.error('获取话题列表失败:', error);
    }
  }, [selectedTopic]);

  const fetchRecords = useCallback(async () => {
    try {
      if (!selectedTopic) {
        setRecords([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        return;
      }
      const response = await tradeAPI.getTradeRecords(selectedTopic);
      const allData = response.data || [];
      const total = allData.length;

      // 前端分页计算
      const startIndex = (pagination.current - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize;
      const currentPageData = allData.slice(startIndex, endIndex);

      setRecords(currentPageData);
      setPagination((prev) => ({ ...prev, total }));
    } catch (error) {
      message.error('获取话题记录失败');
      console.error('获取话题记录失败:', error);
      setRecords([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  }, [selectedTopic, pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  useEffect(() => {
    if (selectedTopic) {
      setLoading(true);
      fetchRecords();
    }
  }, [fetchRecords, selectedTopic]);

  const handleTopicChange = (value) => {
    setLoading(true);
    setSelectedTopic(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleRefresh = () => {
    setLoading(true);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchRecords();
  };

  const handleTableChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
      total: paginationConfig.total,
    });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id, row) => {
        if (id !== null && id !== undefined) return id;
        const obj = parseJsonSafe(row?.raw_payload);
        return obj?.data?.id ?? obj?.id ?? '';
      },
    },
    {
      title: 'raw_data',
      dataIndex: 'raw_payload',
      key: 'raw_data',
      ellipsis: true,
      render: (raw) => {
        const obj = parseJsonSafe(raw);
        const items = getRecordItems(obj);
        const pretty = JSON.stringify(items, null, 2);
        const preview = shortenText(JSON.stringify(items));
        return (
          <Tooltip placement="topLeft" overlayStyle={{ maxWidth: 720 }} title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{pretty}</pre>}>
            <span>{preview}</span>
          </Tooltip>
        );
      },
    },
    {
      title: '操作记录',
      dataIndex: 'raw_payload',
      key: 'op',
      render: (raw) => {
        const obj = parseJsonSafe(raw);
        return <div style={{ whiteSpace: 'pre-wrap' }}>{buildOpText(obj)}</div>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (createdAt) => {
        if (!createdAt) return 'N/A';
        return moment.utc(createdAt).utcOffset(8).format('YYYY-MM-DD HH:mm:ss');
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Title level={2}>
          <FileTextOutlined style={{ marginRight: '8px' }} />
          话题记录
        </Title>

        <div style={{ display: 'flex', gap: '16px' }}>
          <Select placeholder="选择话题" style={{ width: 260 }} allowClear value={selectedTopic} onChange={handleTopicChange}>
            {topics.map((t) => (
              <Option key={t.id} value={t.id}>
                {t.name}
              </Option>
            ))}
          </Select>

          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
            刷新
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={records}
        rowKey="id"
        onChange={handleTableChange}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `显示 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        }}
      />
    </Card>
  );
};

export default TopicRecords;
